import { useMutation } from '@tanstack/react-query';

import { PREFIX_URL } from '@/src/api/config';
import { UpdateEmailFileEntryResponse } from '@/src/components/project/Inbox2/api/updateEmailFileStatus';
import { EmailDeliveryType } from '@/src/components/project/Inbox2/types';
import { axios } from '@/src/lib/axios/axios';
import { MutationConfig, queryClient } from '@/src/lib/react-query/react-query';
import { generateRoutePath } from '@/src/routes';

import { emailFileActivityLogsKeys } from './getEmailFileActivityLogs';
import { emailFileDetailsKeys } from './getEmailFileDetails';

import { emailEntriesKeys } from '../../../api/getEmailEntries';
import { emailEntryKeys } from '../../../api/getEmailEntry';
import { emailThreadAttachmentsKeys } from '../../../api/getEmailThreadAttachments';

// const url = `${PREFIX_URL}/v01/inbox/isRecorded`;
const url = `${PREFIX_URL}/inbox-threads/:inboxEntryId/emails/:emailId/attachments/:fileId`;

export type RecordFileDTO = {
  projectId: string;
  inboxEntryId: number;
  emailId: number;
  fileId: number;
  record: boolean;
};

export const recordFile = ({
  projectId,
  inboxEntryId,
  emailId,
  fileId,
  record,
}: RecordFileDTO): Promise<UpdateEmailFileEntryResponse> => {
  return axios.put(generateRoutePath(url, { projectId, inboxEntryId, emailId, fileId }), {
    record,
  });
};

type useRecordFileOptions = {
  projectId: string;
  inboxEntryId: number;
  emailId: number;
  fileId: number;
  config?: MutationConfig<typeof recordFile>;
};

export const useRecordEmailFile = ({
  inboxEntryId,
  emailId,
  fileId,
  projectId,
  config,
}: useRecordFileOptions) => {
  return useMutation({
    onSuccess: (data) => {
      queryClient.setQueryData(
        [...emailFileDetailsKeys.detail({ projectId, emailId, inboxEntryId, fileId })],
        (prev: any) => {
          return {
            ...prev,
            data: data.data['inbox-email-attachment'],
          };
        }
      );

      // Sync with email entry api detail containing attachments
      queryClient.setQueryData(
        [...emailEntryKeys.detail({ emailId, inboxEntryId, projectId })],
        (prev: any) => {
          return {
            ...prev,
            data: data.data['inbox-email'],
          };
        }
      );

      // Sync with email entries list
      queryClient.setQueriesData(
        [...emailEntriesKeys.lists({ projectId, inboxEntryId })],
        (prev: any) => {
          return {
            ...prev,
            pages: prev.pages.map((page) => {
              return {
                ...page,
                data: {
                  ...page.data,
                  emails: page.data.emails.map((emailEntry) => {
                    if (emailEntry.id === emailId) {
                      return data.data['inbox-email'];
                    }
                    return emailEntry;
                  }),
                },
              };
            }),
          };
        }
      );

      const syncEmailThreadAttachmentsListData = {
        status: data.data['inbox-email-attachment'].status,
        read: data.data['inbox-email-attachment'].read,
        'allowed-status-changes': data.data['inbox-email-attachment']['allowed-status-changes'],
        'change-status': data.data['inbox-email-attachment']['change-status'],
      };

      // Sync with email thread attachments list
      if (
        queryClient.getQueriesData([
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.inbound,
          }),
        ])
      ) {
        queryClient.setQueriesData(
          [
            ...emailThreadAttachmentsKeys.lists({
              inboxEntryId,
              projectId,
              type: EmailDeliveryType.inbound,
            }),
          ],
          (prev: any) => {
            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    attachments: page.data.attachments.map((attachmentItem) => {
                      if (attachmentItem.id === fileId) {
                        return {
                          ...attachmentItem,
                          ...syncEmailThreadAttachmentsListData,
                        };
                      }
                      return attachmentItem;
                    }),
                  },
                };
              }),
            };
          }
        );
      }

      if (
        queryClient.getQueriesData([
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.outbound,
          }),
        ])
      ) {
        queryClient.setQueriesData(
          [
            ...emailThreadAttachmentsKeys.lists({
              inboxEntryId,
              projectId,
              type: EmailDeliveryType.outbound,
            }),
          ],
          (prev: any) => {
            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    attachments: page.data.attachments.map((attachmentItem) => {
                      if (attachmentItem.id === fileId) {
                        return {
                          ...attachmentItem,
                          ...syncEmailThreadAttachmentsListData,
                        };
                      }
                      return attachmentItem;
                    }),
                  },
                };
              }),
            };
          }
        );
      }

      queryClient.invalidateQueries([
        ...emailFileActivityLogsKeys.detail({ projectId, fileId, emailId, inboxEntryId }),
      ]);
    },
    ...config,
    mutationFn: ({ record }) => recordFile({ inboxEntryId, emailId, projectId, fileId, record }),
  });
};
